import React, { useState } from 'react'

import {
  DcButton,
  DcDropDown,
  DcInput,
  DcSwitchTab,
  PixelDiv,
  PixelFactoryContext,
  PixelFlexBox,
  PixelText,
  SectionContent,
} from 'pixel-base'
import { AjaxResponse } from 'rxjs-compat'
import styled from 'styled-components'

import { get, post } from 'utils/api-routes.util'
import { IPixelFactory } from '../../../../../../decl.d'

const AddConfiguration = ({ setConfigurationsModal, setConfigurations }) => {
  const [data, setData] = useState({
    access_token: null,
    wa_phone_number_id: null,
    wa_phone_number: null,
    test_recipient_number: null,
  })

  const [recipientNumberError, setRecipientNumberError] = React.useState({
    error: '',
    field: '',
  })
  const [senderNumberError, setSenderNumberError] = React.useState({
    error: '',
    field: '',
  })
  const [recipientCountry, setRecipientCountry] = React.useState('PK')
  const [senderCountry, setSenderCountry] = React.useState('PK')
  const [error, setError] = React.useState()
  const [environment, setEnvironment] = useState<'test' | 'production'>('test')
  const [configurationCount, setConfigurationCount] = useState<number>(0)

  const { showSuccess, showFailure, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)

  const [isRecipientNoValid, setIsRecipientNoValid] = useState(true)
  const [isSenderNoValid, setIsSenderNoValid] = useState(true)

  const numberValidations = () => {
    const validPatterns = [/^\d{11}$/, /^\+92\d{10}$/, /^\+1\d{10}$/]
    const senderInputNumber = data.wa_phone_number
    const isValidSenderNumber = validPatterns.some((pattern) =>
      pattern.test(senderInputNumber),
    )

    if (environment === 'test') {
      const recipientInputNumber = data.test_recipient_number
      const isValidRecipientNumber = validPatterns.some((pattern) =>
        pattern.test(recipientInputNumber),
      )
      if (!isValidRecipientNumber || !isValidSenderNumber) {
        if (!isValidRecipientNumber) {
          setIsRecipientNoValid(false)
        }
        if (!isValidSenderNumber) {
          setIsSenderNoValid(false)
        }
        return true
      }
    } else if (!isValidSenderNumber) {
      setIsSenderNoValid(false)
      return true
    }

    setIsRecipientNoValid(true)
    setIsSenderNoValid(true)
    return false
  }

  const submitConfigurationsData = () => {
    showPageLoader()
    const endpoint =
      environment === 'production'
        ? '/communication/wa/production-configuration'
        : '/communication/wa/configuration'

    post(endpoint, data).subscribe((res: AjaxResponse<IResponse | any>) => {
      hidePageLoader()
      if (res.response.data) {
        setConfigurationsModal(false)
        setConfigurations(res.response.data)
        showSuccess('Added Successfully')
      } else {
        setError(res.response.error || 'Something went wrong')
        showFailure(res.response.error || 'Something went wrong')
      }
    })
  }

  const setValidationsErrors = () => {
    if (!isSenderNoValid) {
      const senderCountryCode = senderCountry == 'PK' ? '+92' : '+1'
      setSenderNumberError({
        field: 'phone_no',
        error: `Phone number format should be like this ${senderCountryCode}3000000000`,
      })
    }
    if (environment === 'test' && !isRecipientNoValid) {
      const recipientCountryCode = recipientCountry == 'PK' ? '+92' : '+1'
      setRecipientNumberError({
        field: 'phone_no',
        error: `Phone number format should be like this ${recipientCountryCode}3000000000`,
      })
    }
  }

  const removeValidationErrors = () => {
    setRecipientNumberError({
      error: '',
      field: '',
    })
    setSenderNumberError({
      error: '',
      field: '',
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const submitErros = numberValidations()
    setValidationsErrors()
    if (!submitErros) {
      removeValidationErrors()
      submitConfigurationsData()
    }
  }

  const recipientCountryChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (recipientCountry === 'PK') {
      if (e.target.value.length <= 13) {
        const newValue = e.target.value
        if (e.target.value.startsWith('+92')) {
          setData({
            ...data,
            test_recipient_number: newValue,
          })
        }
      }
    } else if (recipientCountry === 'US') {
      if (e.target.value.length <= 12) {
        const newValue = e.target.value
        if (newValue.startsWith('+1')) {
          setData({
            ...data,
            test_recipient_number: newValue,
          })
        }
      }
    }
    removeValidationErrors()
  }

  const senderCountryChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (senderCountry === 'PK') {
      if (e.target.value.length <= 13) {
        const newValue = e.target.value
        if (e.target.value.startsWith('+92')) {
          setData({
            ...data,
            wa_phone_number: newValue,
          })
        }
      }
    } else if (senderCountry === 'US') {
      if (e.target.value.length <= 12) {
        const newValue = e.target.value
        if (newValue.startsWith('+1')) {
          setData({
            ...data,
            wa_phone_number: newValue,
          })
        }
      }
    }
    removeValidationErrors()
  }

  React.useEffect(() => {
    const newData = { ...data }

    if (senderCountry === 'PK') {
      newData.wa_phone_number = '+92'
    } else if (senderCountry === 'US') {
      newData.wa_phone_number = '+1'
    }

    if (recipientCountry === 'PK') {
      newData.test_recipient_number = '+92'
    } else if (recipientCountry === 'US') {
      newData.test_recipient_number = '+1'
    }
    setData(newData)
  }, [recipientCountry, senderCountry])

  React.useEffect(() => {
    get('/communication/wa/configuration').subscribe(
      (res: any) => {
        const configs = res?.response?.data
        const count = Array.isArray(configs) ? configs.length : 0
        setConfigurationCount(count)

        if (count === 0) {
          setEnvironment('test')
        } else if (count === 1) {
          setEnvironment('production')
        }
      },
      () => {
        setConfigurationCount(0)
        setEnvironment('test')
      },
    )
  }, [])

  return (
    <Form onSubmit={handleSubmit}>
      <SectionContent>
        <FormGroup>
          <PixelDiv style={{ width: '100%', marginBottom: '10px' }}>
            <DcSwitchTab
              className="full-width"
              tabs={[
                {
                  value: 'test',
                  label: 'Test configuration',
                  disabled: configurationCount === 1,
                },
                {
                  value: 'production',
                  label: 'Production configuration',
                  disabled: configurationCount === 0,
                },
              ]}
              value={environment}
              onChange={(val) => setEnvironment(val as 'test' | 'production')}
            />
          </PixelDiv>
        </FormGroup>
        <FormGroup>
          <PixelText customColor="red" textSize="80%">
            NOTE: Please make sure you have added the test configuration first
            before adding the production configuration.
          </PixelText>
        </FormGroup>
        <FormGroup>
          <DcInput
            as="textarea"
            isShowLabel={true}
            customLabel="Access Token"
            label="Access Token"
            required
            type={'text'}
            name={'Access Token'}
            placeholder={'Access Token'}
            value={data.access_token}
            onChange={(e) => setData({ ...data, access_token: e.target.value })}
          />
        </FormGroup>

        <FormGroup>
          <PixelDiv style={{ width: '100%', marginBottom: '10px' }}>
            <DcInput
              isShowLabel={true}
              customLabel="Phone ID"
              label="Phone ID"
              required
              type={'number'}
              name={'Phone ID'}
              placeholder={'Phone ID'}
              value={data.wa_phone_number_id}
              onChange={(e) =>
                setData({ ...data, wa_phone_number_id: e.target.value })
              }
            />
          </PixelDiv>
        </FormGroup>

        <PixelFlexBox
          style={{
            height: 'auto',
            marginBottom: '10px',
            gap: '10px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <PixelFlexBox gap="10px">
            <PixelDiv
              style={{
                width: '60%',
              }}
            >
              <DcDropDown
                isShowLabel={true}
                customLabel="Country"
                label="Country"
                placeholder={'Select Country'}
                options={[
                  {
                    label: 'Pakistan',
                    value: 'PK',
                  },
                  {
                    label: 'United States',
                    value: 'US',
                  },
                ]}
                value={senderCountry}
                onChange={(e) => {
                  setSenderCountry(e.target.value)
                }}
              />{' '}
            </PixelDiv>

            <PixelDiv style={{ width: '75%' }}>
              <DcInput
                isShowLabel={true}
                customLabel="Phone Number"
                label="Phone Number"
                type="text"
                value={data.wa_phone_number}
                onChange={senderCountryChange}
                required
                name="Phone Number"
                placeholder="Phone Number"
              />
              {senderNumberError.error != '' && (
                <PixelText customColor="red" textSize="80%">
                  {senderNumberError?.error}
                </PixelText>
              )}
            </PixelDiv>
          </PixelFlexBox>
        </PixelFlexBox>

        {environment === 'test' && (
          <PixelFlexBox
            style={{
              height: 'auto',
              marginBottom: '10px',
              gap: '10px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PixelFlexBox gap="10px">
              <PixelDiv
                style={{
                  width: '60%',
                }}
              >
                <DcDropDown
                  isShowLabel={true}
                  customLabel="Country"
                  label="Country"
                  placeholder={'Select Country'}
                  options={[
                    {
                      label: 'Pakistan',
                      value: 'PK',
                    },
                    {
                      label: 'United States',
                      value: 'US',
                    },
                  ]}
                  value={recipientCountry}
                  onChange={(e) => {
                    setRecipientCountry(e.target.value)
                  }}
                />
              </PixelDiv>

              <PixelDiv style={{ width: '75%' }}>
                <DcInput
                  isShowLabel={true}
                  customLabel="Test Recipient Number"
                  label="Test Recipient Number"
                  type="text"
                  value={data.test_recipient_number}
                  onChange={recipientCountryChange}
                  required
                  name="Test Recipient Number"
                  placeholder="Test Recipient Number"
                />
                {recipientNumberError.error != '' && (
                  <PixelText customColor="red" textSize="80%">
                    {recipientNumberError?.error}
                  </PixelText>
                )}
              </PixelDiv>
            </PixelFlexBox>
          </PixelFlexBox>
        )}
        {error && <PixelText customColor="red">Error : {error}</PixelText>}
        <FormGroup flexEnd>
          <DcButton size="sm" variant="primary" type="submit">
            Save
          </DcButton>
        </FormGroup>
      </SectionContent>
    </Form>
  )
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-start;
  align-content: flex-start;
  gap: 10px;
`

const FormGroup = styled.div<{ flexEnd?: boolean }>`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: ${(props) => (props.flexEnd ? 'flex-end' : 'center')};
  align-items: flex-start;
  align-content: flex-start;
  width: 100%;
  flex-grow: 1;
  gap: 10px;
  .full-width {
    flex-grow: 1;
  }
`

export default AddConfiguration
